import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IPartnerFinSobRow } from '../../model'

import { getQuotaRows } from './getQuotaRows'
import { getRevenueMeasureField } from './getRevenueMeasureField'
import { getWeightedSob, TFinSobData } from './getWeightedSob'
import { sumMeasure } from './sumMeasure'
import { IRevenueAndSobFigures, TCategoryRowScope } from './types'

const EMPTY_QUOTA: IRevenueAndSobFigures = {
  sales: null,
  sob: null,
}

interface IParams {
  rows: IPartnerFinSobRow[]
  /** The financial year the tile is showing. Like actuals, quota reads this same year, as-is. */
  selectedFYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  category?: string
  categoryRowScope?: TCategoryRowScope
}

/** The tile's quota column: rows flagged `isQuota`, scoped to the selected year/category — a
 *  target set independently of the partner's own plan.
 *
 *  Revenue is additive, so it is a plain sum. SOB is a share, so it only rolls up as an average
 *  weighted by the same revenue measure. No YTD proration, unlike `plan`/`baseline` — quota is
 *  reported as recorded. */
export const getRevenueAndSobQuota = ({
  rows,
  selectedFYear,
  actualsType,
  isLocalCurrency,
  category,
  categoryRowScope,
}: IParams): IRevenueAndSobFigures => {
  const measureField = getRevenueMeasureField(actualsType, isLocalCurrency)

  if (measureField === null) {
    return EMPTY_QUOTA
  }

  const quotaRows = getQuotaRows({
    rows,
    selectedFYear,
    category,
    categoryRowScope,
  })

  const rowData = quotaRows.map<TFinSobData>((row) => ({
    sob: row.sob,
    revenue: row[measureField],
  }))

  const totalSales = sumMeasure(quotaRows, measureField)
  const weightedSob = getWeightedSob(rowData)

  return {
    sales: totalSales,
    sob: weightedSob,
  }
}
